import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createRun,
  getEngagement,
  listEntities,
  listOrganisations,
  myAuthority,
  listPublishedVersions,
  listRuns,
} from "../lib/api";
import type {
  Engagement,
  MembershipRole,
  Organisation,
  PublishedVersion,
  Run,
} from "../lib/types";
import { useBreadcrumb } from "../shell/ContextHeader";
import { StateBadge } from "./RunOverview";

// One engagement: its assessment runs, and the place a new run starts from.
// The architect's session 2 direction §3.1: the run-creation workflow STARTS
// from an engagement — so this screen is the only place the console offers
// "New assessment run", and the payload it sends is the minimal one 0007
// §1 derives everything else from.

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      org: Organisation;
      engagement: Engagement;
      entityName: string;
      role: MembershipRole | null;
      runs: Run[];
      versions: PublishedVersion[];
    };

export function EngagementDetail() {
  const { orgId, engId } = useParams<{ orgId: string; engId: string }>();
  const { setTrail } = useBreadcrumb();
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(async () => {
    if (!orgId || !engId) return;
    try {
      const [orgs, role, engagement, runs, versions] = await Promise.all([
        listOrganisations(),
        myAuthority(orgId),
        getEngagement(engId),
        listRuns(engId),
        listPublishedVersions(),
      ]);
      const org = orgs.find((o) => o.id === orgId);
      if (!org || !engagement || engagement.organisation_id !== orgId) {
        setState({ kind: "error", message: "engagement not found, or you are not a member" });
        return;
      }
      const entities = await listEntities(orgId);
      const entityName =
        entities.find((e) => e.id === engagement.entity_id)?.name ?? "—";
      setState({ kind: "ready", org, engagement, entityName, role, runs, versions });
      setTrail([
        { label: "Clients & engagements", to: "/clients" },
        { label: org.name, to: `/clients/${org.id}` },
        { label: engagement.name },
      ]);
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [orgId, engId, setTrail]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") return <p>Loading…</p>;
  if (state.kind === "error")
    return <div className="error-banner">Could not load: {state.message}</div>;

  const { org, engagement, entityName, role, runs, versions } = state;
  const canWrite = role === "owner" || role === "lead_assessor" || role === "assessor";
  const archived = engagement.status === "archived";

  return (
    <>
      <h1>{engagement.name}</h1>
      <p style={{ color: "var(--text-muted)", marginTop: 0 }}>
        Engagement against <strong>{entityName}</strong>
        {archived && (
          <>
            {" "}
            <span className="badge badge-archived">archived</span>
          </>
        )}
      </p>

      {/* The main action sits under the title (UI review slice 2). */}
      {canWrite && !archived && (
        <NewRunForm orgId={org.id} engagementId={engagement.id} versions={versions} />
      )}
      {archived && (
        <p style={{ color: "var(--text-muted)" }}>
          An archived engagement takes no new runs. Restore it first.
        </p>
      )}

      <section style={{ marginTop: "1rem" }}>
        <h2>Assessment runs</h2>
        {runs.length === 0 ? (
          <div className="card empty-state">
            <h3>No assessment runs yet</h3>
            <p>
              A run assesses this engagement&apos;s entity against one published
              instrument version, locked at creation. Findings, actions and
              reports all hang off a run.
            </p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>State</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/clients/${org.id}/runs/${r.id}`}>
                        {r.title ?? "Untitled run"}
                      </Link>
                    </td>
                    <td>
                      <StateBadge state={r.state} />
                    </td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </section>
    </>
  );
}

function NewRunForm({
  orgId,
  engagementId,
  versions,
}: {
  orgId: string;
  engagementId: string;
  versions: PublishedVersion[];
}) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [versionId, setVersionId] = useState(versions.length === 1 ? versions[0]!.id : "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [changingVersion, setChangingVersion] = useState(false);

  const chosen = versions.find((v) => v.id === versionId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const run = await createRun({
        organisation_id: orgId,
        engagement_id: engagementId,
        instrument_version_id: versionId,
        title: title.trim(),
      });
      navigate(`/clients/${orgId}/runs/${run.id}`);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h3 style={{ marginTop: 0 }}>New assessment run</h3>
      {error && <div className="error-banner">{error}</div>}
      <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="field" style={{ marginBottom: 0, flex: "1 1 200px" }}>
          <label htmlFor="run-title">Title</label>
          <input
            id="run-title"
            required
            value={title}
            placeholder="e.g. Q3 baseline"
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="field" style={{ marginBottom: 0, minWidth: 280 }}>
          <label htmlFor="run-version">Instrument</label>
          {/* Rarely changed: one line with "change…" (UI review slice 4). */}
          {chosen && !changingVersion ? (
            <span style={{ padding: "0.45rem 0" }}>
              {chosen.instruments?.title ?? chosen.id} — v{chosen.version}
              {" · "}
              <button type="button" className="btn-link" onClick={() => setChangingVersion(true)}>
                change…
              </button>
            </span>
          ) : (
            <select
              id="run-version"
              required
              value={versionId}
              onChange={(e) => {
                setVersionId(e.target.value);
                setChangingVersion(false);
              }}
            >
              <option value="">Choose…</option>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.instruments?.title ?? v.id} — v{v.version}
                </option>
              ))}
            </select>
          )}
        </div>
        <button className="btn" type="submit" disabled={busy || title.trim() === "" || versionId === ""}>
          Create run
        </button>
      </div>
      {chosen && (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--lf-meta-size)", marginBottom: 0 }}>
          This run will be locked to {chosen.instruments?.title} v{chosen.version} and
          will not change if the instrument is updated later.
        </p>
      )}
    </form>
  );
}
